// Catálogo central de metadados de UI: ferramentas, nós e supervisão.
// OBS: o catálogo de MODELOS agora é dinâmico (vem do backend) — ver
// src/store/modelsStore.ts. Use `useModelsStore` para rótulos/grupos/permitidos.
import type { ToolId, NodeKind, HumanOversightMode } from '../types';
import { nodeColors } from '../theme/colors';
import {
  Webhook,
  Bot,
  Sparkles,
  Wrench,
  GitBranch,
  Repeat,
  Database,
  BookOpen,
  UserCheck,
  LogOut,
  Search,
  Calculator,
  Globe,
  Code2,
  TableProperties,
  Eye,
  EyeOff,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';

export const TOOL_META: Record<ToolId, { label: string; icon: LucideIcon }> = {
  web_search: { label: 'Web Search', icon: Search },
  rag: { label: 'RAG / Base de Conhecimento', icon: BookOpen },
  calculator: { label: 'Calculadora', icon: Calculator },
  http: { label: 'HTTP Request', icon: Globe },
  code: { label: 'Code Interpreter', icon: Code2 },
  sql: { label: 'SQL Query', icon: Database },
};

export const TOOL_OPTIONS = Object.entries(TOOL_META).map(([value, meta]) => ({
  value: value as ToolId,
  label: meta.label,
  icon: meta.icon,
}));

export const MEMORY_LABELS: Record<'none' | 'short' | 'long', string> = {
  none: 'Nenhuma',
  short: 'Curto prazo',
  long: 'Longo prazo',
};

export interface NodeTypeDef {
  kind: NodeKind;
  label: string;
  category: string;
  color: string;
  icon: LucideIcon;
  description: string;
}

export const NODE_TYPES: NodeTypeDef[] = [
  {
    kind: 'trigger',
    label: 'Trigger / Input',
    category: 'Entrada',
    color: nodeColors.trigger,
    icon: Webhook,
    description: 'Inicia o fluxo (manual, webhook, agendado).',
  },
  {
    kind: 'agent',
    label: 'Agent',
    category: 'Núcleo',
    color: nodeColors.agent,
    icon: Bot,
    description: 'Agente existente — herda persona e ferramentas.',
  },
  {
    kind: 'llm',
    label: 'LLM',
    category: 'Núcleo',
    color: nodeColors.llm,
    icon: Sparkles,
    description: 'Chamada direta a um modelo com prompt.',
  },
  {
    kind: 'tool',
    label: 'Tool',
    category: 'Ação',
    color: nodeColors.tool,
    icon: Wrench,
    description: 'Executa uma ferramenta (RAG, HTTP, SQL...).',
  },
  {
    kind: 'router',
    label: 'Router / Condition',
    category: 'Controle',
    color: nodeColors.router,
    icon: GitBranch,
    description: 'Ramifica o fluxo (if/else, switch).',
  },
  {
    kind: 'loop',
    label: 'Loop',
    category: 'Controle',
    color: nodeColors.loop,
    icon: Repeat,
    description: 'Itera um corpo até a condição de parada.',
  },
  {
    kind: 'memory',
    label: 'Memory',
    category: 'Estado',
    color: nodeColors.memory,
    icon: Database,
    description: 'Lê/escreve memória de curto ou longo prazo.',
  },
  {
    kind: 'kb',
    label: 'Knowledge Base (RAG)',
    category: 'Dados',
    color: nodeColors.kb,
    icon: BookOpen,
    description: 'Recupera trechos de uma base de conhecimento.',
  },
  {
    kind: 'human_review',
    label: 'Human Review',
    category: 'Supervisão',
    color: nodeColors.human_review,
    icon: UserCheck,
    description: 'Pausa para decisão humana (aprovar/rejeitar/editar).',
  },
  {
    kind: 'output',
    label: 'Output',
    category: 'Saída',
    color: nodeColors.output,
    icon: LogOut,
    description: 'Saída final do fluxo (texto, JSON, destino).',
  },
];

export const NODE_DEF_BY_KIND: Record<NodeKind, NodeTypeDef> = NODE_TYPES.reduce(
  (acc, def) => {
    acc[def.kind] = def;
    return acc;
  },
  {} as Record<NodeKind, NodeTypeDef>,
);

export const NODE_CATEGORIES = Array.from(new Set(NODE_TYPES.map((n) => n.category)));

export interface OversightDef {
  mode: HumanOversightMode;
  label: string;
  short: string;
  icon: LucideIcon;
  description: string;
  color: string;
}

export const OVERSIGHT_MODES: OversightDef[] = [
  {
    mode: 'in_the_loop',
    label: 'Human-in-the-loop',
    short: 'No loop',
    icon: UserCheck,
    description:
      'O fluxo pausa e aguarda decisão humana em pontos definidos antes de prosseguir. Nada avança sem aprovação.',
    color: '#E11019',
  },
  {
    mode: 'on_the_loop',
    label: 'Human-on-the-loop',
    short: 'Sobre o loop',
    icon: Eye,
    description:
      'O fluxo roda sozinho, mas o humano monitora em tempo real e pode pausar, intervir ou abortar a qualquer momento.',
    color: '#1FAA6B',
  },
  {
    mode: 'out_of_the_loop',
    label: 'Human-out-of-the-loop',
    short: 'Fora do loop',
    icon: EyeOff,
    description:
      'Execução totalmente autônoma, sem intervenção. O humano só vê o resultado e a auditoria depois.',
    color: '#5B6B7B',
  },
];

export const OVERSIGHT_BY_MODE: Record<HumanOversightMode, OversightDef> =
  OVERSIGHT_MODES.reduce(
    (acc, def) => {
      acc[def.mode] = def;
      return acc;
    },
    {} as Record<HumanOversightMode, OversightDef>,
  );

export const LOOP_MODE_LABELS: Record<string, string> = {
  for_each: 'For-each',
  while: 'While',
  until: 'Until-condition',
  max_iterations: 'Max-iterations',
};

export const FLOW_STATUS_LABELS: Record<'draft' | 'published' | 'inactive', string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  inactive: 'Inativo',
};

export const ComplianceIcon = ShieldCheck;
export const TableIcon = TableProperties;
